/**
 * Stores Table Component
 *
 * Component for displaying stores financial data in a table
 */

import React, {useState} from 'react';
import {StoreFinancial} from '../models/report.model';
import AppTable from '../../../Presentation/components/ui/AppTable';
import {AppText} from '../../../Presentation/components/ui/Typography';
import {TableColumn} from '../../../Presentation/components/ui/AppTable';
import DateTimeDisplay from '../../../Presentation/components/ui/DateTimeDisplay';
import {formatCurrency} from '../../../Presentation/utils/numberUtils';

interface StoresTableProps {
  stores: StoreFinancial[];
}

export const StoresTable: React.FC<StoresTableProps> = ({stores}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Calculate pagination
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = stores.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const getPaymentStatusBadge = (status: string) => {
    const statusConfig = {
      paid: {label: 'مدفوع', color: 'bg-green-100 text-green-800'},
      partial: {label: 'مدفوع جزئياً', color: 'bg-yellow-100 text-yellow-800'},
      unpaid: {label: 'غير مدفوع', color: 'bg-red-100 text-red-800'}
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.unpaid;

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };


  const columns: TableColumn<StoreFinancial>[] = [
    {
      key: 'store_name',
      title: 'اسم المتجر',
      render: (store) => <AppText>{store.store_name}</AppText>
    },
    {
      key: 'invoice_value',
      title: 'قيمة الفاتورة',
      render: (store) => <AppText>{formatCurrency(store.invoice_value)}</AppText>
    },
    {
      key: 'paid_amount',
      title: 'المدفوع',
      render: (store) => <AppText>{formatCurrency(store.paid_amount)}</AppText>
    },
    {
      key: 'remaining_balance',
      title: 'المتبقي',
      render: (store) => <AppText>{formatCurrency(store.remaining_balance)}</AppText>
    },
    {
      key: 'payment_status',
      title: 'حالة الدفع',
      render: (store) => getPaymentStatusBadge(store.payment_status)
    },
    {
      key: 'last_payment_date',
      title: 'آخر دفع',
      render: (store) => <DateTimeDisplay dateString={store.last_payment_date} />
    }
  ];

  return (
    <AppTable
      columns={columns}
      data={paginatedData}
      rowKey={(store) => store.store_id}
      emptyText="لا توجد متاجر متاحة"
      hidePagination={false}
      page={currentPage}
      pageSize={pageSize}
      totalCount={stores.length}
      onPageChange={handlePageChange}
      onPageSizeChange={handlePageSizeChange}
    />
  );
};
